import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Value,
} from 'react-phone-number-input';
import { User } from '/types/user-type';
import {
  ACCOUNT_QUERY_KEY,
  useUpdateAccount,
} from 'features/profile/hooks/use-account';
import { useQueryClient } from '@tanstack/react-query';

import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { CountrySelect } from 'components/ui/country-select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from 'components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'components/ui/command';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from 'lib/utils';
import { IamData } from 'features/iam/services/user-service';
import UIPhoneInput from 'components/core/phone-input/phone-input';
import { Badge } from 'components/ui/badge';
import { useGetRolesQuery } from 'features/iam/hooks/use-iam';
import { useOrganizations } from 'features/organization/hooks/use-organization';
import { useTranslation } from 'react-i18next';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { ORGANIZATION_CONSTANTS } from 'constant/organization';
import { ROLES } from 'constant/roles';

/**
 * `EditIamProfileDetails` component allows the user to edit their profile details, including their full name, email, phone number, country, and roles.
 * It integrates with the backend to fetch available roles, updates the account, and provides role selection with a limit of 5 roles.
 * The component supports form validation with mandatory country field and ensures the changes are saved to the server.
 *
 * Features:
 * - Full name editing
 * - Email display (read-only)
 * - Phone number with international format
 * - Searchable country selection with flags (mandatory)
 * - Role management with badge display
 * - Organization assignment for org-admin and org-user roles
 * - Form change detection
 * - Validation for required fields
 *
 * @component
 * @example
 * const userInfo = {
 *   fullName: 'John Doe',
 *   email: 'john.doe@example.com',
 *   phoneNumber: '+1234567890',
 *   country: 'United States',
 *   roles: ['admin', 'user'],
 *   itemId: '12345'
 * };
 *
 * <EditIamProfileDetails userInfo={userInfo} onClose={() => {}} />
 *
 * @param {Object} props - The component's props
 * @param {User | IamData} props.userInfo - The user information object containing current details to be edited
 * @param {Function} props.onClose - Callback function to close the dialog/modal
 *
 * @returns {React.Element} The rendered component
 */

type FormData = {
  itemId: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  country: string;
  roles: string[];
  currentRole: string;
  organizationId: string;
};

type EditIamProfileDetailsProps = {
  userInfo: User | IamData;
  onClose: () => void;
};

const MAX_ROLES = 5;

export const EditIamProfileDetails: React.FC<EditIamProfileDetailsProps> = ({
  userInfo,
  onClose,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [availableRoles, setAvailableRoles] = useState<
    Array<{
      name: string;
      slug: string;
    }>
  >([]);
  const [organizationSearchTerm, setOrganizationSearchTerm] = useState('');
  const [organizationOpen, setOrganizationOpen] = useState(false);
  const isAdmin = useCommonStore((state) => state.isAdmin);
  const isOrgAdmin = useCommonStore((state) => state.isOrgAdmin);

  const { data: rolesData, isLoading: isLoadingRoles } = useGetRolesQuery({
    page: 0,
    pageSize: 100,
    filter: { search: '' },
    sort: { property: 'name', isDescending: false },
  });

  // Fetch organizations for autocomplete
  const { data: organizationsData } = useOrganizations({
    page: 0,
    pageSize: 50,
    name: organizationSearchTerm,
    enabled: true,
  });

  const organizations = (organizationsData?.data || []).filter(
    org => org.name !== ORGANIZATION_CONSTANTS.DEFAULT_ORGANIZATION_NAME
  );

  useEffect(() => {
    if (rolesData?.data) {
      const mappedRoles = rolesData.data
        .filter(role => {
          if (isOrgAdmin) {
            // remove app-user and admin roles for org-admin users from UI
            return role.name !== 'app-user' && role.name !== 'admin';
          }
          // remove app-user role for admin users from UI
          return role.name !== 'app-user';
        })
        .map((role) => ({
          name: role.name,
          slug: role.name.toLowerCase().replace(/\s+/g, '-'),
        }));
      if (mappedRoles) setAvailableRoles(mappedRoles);
    }
  }, [isOrgAdmin, rolesData]);

  const { mutate: updateAccount, isPending } = useUpdateAccount({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEY });

      await queryClient.refetchQueries({
        queryKey: ACCOUNT_QUERY_KEY,
        type: 'active',
        exact: false,
      });

      onClose();
      window.location.reload();
    },
  });

  const [isFormChanged, setIsFormChanged] = useState(false);

  const form = useForm<FormData>({
    defaultValues: {
      itemId: '',
      fullName: '',
      email: '',
      phoneNumber: '',
      country: '',
      roles: [],
      currentRole: '',
      organizationId: '',
    },
  });

  const { watch, setValue, handleSubmit, control } = form;
  const watchedValues = watch();
  const selectedOrganizationId = watch('organizationId');
  const selectedOrganization = organizations.find(org => org.itemId === selectedOrganizationId);
  const isOrgAdminOrUser = watchedValues.roles?.includes(ROLES.ORG_ADMIN) || watchedValues.roles?.includes(ROLES.ORG_USER);

  useEffect(() => {
    if (userInfo) {
      setValue('fullName', `${userInfo.firstName ?? ''} ${userInfo.lastName ?? ''}`.trim());
      setValue('email', userInfo.email ?? '');
      setValue('phoneNumber', userInfo.phoneNumber ?? '');
      setValue('country', userInfo.country ?? '');
      setValue('itemId', userInfo.itemId ?? '');

      // Handle organization assignment for edit mode
      let organizationId = '';
      if ('organizationId' in userInfo) {
        // For cases where userInfo has organizationId directly
        organizationId = (userInfo as any).organizationId ?? '';
      } else if ('organizations' in userInfo && Array.isArray((userInfo as any).organizations)) {
        // For IamData type with organizations array, get the first organization
        const organizations = (userInfo as any).organizations;
        if (organizations.length > 0) {
          const firstOrg = organizations[0];
          // Don't set organization if it's 'Default Organization'
          if (firstOrg.name !== ORGANIZATION_CONSTANTS.DEFAULT_ORGANIZATION_NAME) {
            organizationId = firstOrg.itemId;
          }
        }
      }
      setValue('organizationId', organizationId);

      if (userInfo.roles && Array.isArray(userInfo.roles)) {
        const tempRole = userInfo.roles
          .filter(role => role !== 'app-user');
        setValue('roles', tempRole.slice(0, MAX_ROLES));
      }
    }
  }, [userInfo, setValue]);

  useEffect(() => {
    if (!userInfo) return;

    // Get initial organization ID
    let initialOrganizationId = '';
    if ('organizationId' in userInfo) {
      initialOrganizationId = (userInfo as any).organizationId ?? '';
    } else if ('organizations' in userInfo && Array.isArray((userInfo as any).organizations)) {
      const organizations = (userInfo as any).organizations;
      if (organizations.length > 0) {
        const firstOrg = organizations[0];
        // Don't set organization if it's 'Default Organization'
        if (firstOrg.name !== ORGANIZATION_CONSTANTS.DEFAULT_ORGANIZATION_NAME) {
          initialOrganizationId = firstOrg.itemId;
        }
      }
    }

    const initialValues = {
      fullName: `${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim(),
      phoneNumber: userInfo.phoneNumber || '',
      country: userInfo.country || '',
      profileImageUrl: userInfo.profileImageUrl || '',
      roles: userInfo.roles || [],
      organizationId: initialOrganizationId,
    };

    const rolesEqual =
      Array.isArray(watchedValues.roles) &&
      Array.isArray(initialValues.roles) &&
      watchedValues.roles.length === initialValues.roles.length &&
      watchedValues.roles.every((role) => initialValues.roles.includes(role));

    setIsFormChanged(
      watchedValues.fullName !== initialValues.fullName ||
      watchedValues.phoneNumber !== initialValues.phoneNumber ||
      watchedValues.country !== initialValues.country ||
      watchedValues.organizationId !== initialValues.organizationId ||
      !rolesEqual
    );
  }, [watchedValues, userInfo]);

  const onSubmit = async (data: FormData) => {
    const names = data.fullName.trim().split(' ');
    const firstName = names[0] || '';
    const lastName = names.slice(1).join(' ') || '';

    const tempRole = data.roles ?? [];
    tempRole.push('app-user');
    const payload = {
      itemId: data.itemId,
      firstName,
      lastName,
      email: data.email,
      phoneNumber: data.phoneNumber,
      country: data.country,
      roles: tempRole,
      organizationIds: isAdmin ? (data.organizationId == "" ? [] : [data.organizationId]) : organizationsData?.data?.map((org) => org.itemId),
    };

    updateAccount(payload);
  };

  const handleAddRole = (roleSlug: string) => {
    if (!roleSlug) return;

    const currentRoles = form.getValues('roles') || [];

    if (currentRoles.length >= MAX_ROLES) return;

    if (!currentRoles.includes(roleSlug)) {
      setValue('roles', [...currentRoles, roleSlug]);
    }

    if (roleSlug === "admin" && isAdmin) {
      setValue('organizationId', '');
    }

    setValue('currentRole', '');
  };

  const handleRemoveRole = (roleToRemove: string) => {
    const currentRoles = form.getValues('roles') || [];
    setValue(
      'roles',
      currentRoles.filter((role) => role !== roleToRemove)
    );

    if (roleToRemove == ROLES.ORG_ADMIN || roleToRemove == ROLES.ORG_USER) {
      setValue('organizationId', '');
    }
  };

  const handleDialogClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  const getAvailableRoles = () => {
    const selectedRoles = form.getValues('roles') || [];
    return availableRoles.filter((role) => !selectedRoles.includes(role.slug));
  };

  /*  const getRoleNameBySlug = (slug: string) => {
     const role = availableRoles.find((r) => r.slug === slug);
     return role ? role.name : slug;
   }; */

  const isMaxRolesReached = () => {
    const selectedRoles = form.getValues('roles') || [];
    return selectedRoles.length >= MAX_ROLES;
  };

  return (
    <DialogContent
      className="sm:max-w-[700px] overflow-y-auto max-h-screen"
      onClick={handleDialogClick}
    >
      <DialogHeader>
        <DialogTitle>{t('EDIT_PROFILE_DETAILS')}</DialogTitle>
        <DialogDescription>{t('KEEP_DETAILS_ACCURATE_UP_TO_DATE')}</DialogDescription>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={control}
              name="fullName"
              rules={{ required: t('FULL_NAME_REQUIRED') }}
              render={({ field }) => (
                <FormItem>
                  <Label>{t('FULL_NAME')}*</Label>
                  <FormControl>
                    <Input {...field} placeholder={t('ENTER_YOUR_FULL_NAME')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <Label>{t('EMAIL')}</Label>
                  <FormControl>
                    <Input {...field} disabled />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={control}
              name="currentRole"
              rules={{ required: isAdmin || watchedValues.roles.length > 0 ? false : t('ROLES_REQUIRED') }}
              render={({ field }) => (
                <FormItem>
                  <Label>
                    {t('ROLES')} {isOrgAdmin ? "*" : ""}
                  </Label>
                  <div className="space-y-2">
                    <Select
                      onValueChange={(value) => {
                        if ((value == "admin" && isAdmin)) {
                          setValue('organizationId', '');
                        }
                        handleAddRole(value);
                      }}
                      value={field.value}
                      disabled={isMaxRolesReached() || isLoadingRoles}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              isLoadingRoles
                                ? `${t('LOADING_ROLES')}...`
                                : isMaxRolesReached()
                                  ? t('MAX_ROLES_REACHED')
                                  : t('SELECT_ROLES')
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {getAvailableRoles().map((role) => (
                          <SelectItem key={role.slug} value={role.slug}>
                            <span>{t(role.slug?.toUpperCase())}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <div className="flex flex-wrap gap-2 mt-2">
                      {watchedValues.roles?.map((roleSlug) => (
                        <Badge
                          key={roleSlug}
                          className="pr-1 flex items-center gap-1 text-white hover:bg-primary"
                        >
                          <span>{t(roleSlug?.toUpperCase())}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            className="h-4 w-4 p-0 hover:bg-transparent"
                            onClick={() => handleRemoveRole(roleSlug)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      ))}
                    </div>
                    {/* {watchedValues.roles?.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {watchedValues.roles.length} of {MAX_ROLES} {t('ROLES_SELECTED')}
                      </p>
                    )} */}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {isAdmin && (
              <FormField
                control={control}
                name="organizationId"
                rules={{ required: isOrgAdminOrUser ? t('ORG_ADMIN_ORG_USER_ORG_REQUIRED') : false }}
                render={({ field }) => (
                  <FormItem>
                    <Label className="text-high-emphasis">{t('ORGANIZATION')} {isOrgAdminOrUser ? "*" : ""}</Label>
                    {watchedValues.roles?.includes(ROLES.ADMIN) && (
                      <p className="text-sm text-yellow-600 mb-2">
                        {t('ADMIN_ORG_WARNING')}
                      </p>
                    )}
                    {/* {watchedValues.roles?.includes(ROLES.ORG_ADMIN) && !field.value && (
                      <p className="text-sm text-yellow-600 mb-2">
                        You must select an organization for org-admin and org-user roles.
                      </p>
                    )} */}
                    <Popover open={organizationOpen} onOpenChange={setOrganizationOpen}>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={organizationOpen}
                            className={cn(
                              "w-full justify-between h-11 rounded-md",
                              !field.value && "text-muted-foreground"
                            )}
                            disabled={watchedValues.roles?.includes('admin') || !watchedValues.roles?.length}
                          >
                            {selectedOrganization ? selectedOrganization.name : t('SELECT_ORGANIZATION')}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0" align="start">
                        <Command className="overflow-hidden">
                          <CommandInput
                            placeholder={t('SEARCH_ORGANIZATIONS')}
                            value={organizationSearchTerm}
                            onValueChange={setOrganizationSearchTerm}
                          />
                          <CommandList className="h-[200px] overflow-auto py-2">
                            <CommandEmpty>{t('NO_ORGANIZATIONS_FOUND')}</CommandEmpty>
                            <CommandGroup>
                              {organizations.map((organization) => (
                                <CommandItem
                                  key={organization.itemId}
                                  value={organization.name}
                                  onSelect={() => {
                                    setValue('organizationId', organization.itemId);
                                    setOrganizationOpen(false);
                                  }}
                                  className="cursor-pointer"
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4 shrink-0",
                                      selectedOrganizationId === organization.itemId ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  <div className="flex flex-col min-w-0 flex-1">
                                    <span className="font-medium truncate">{organization.name}</span>
                                    <span className="text-sm text-muted-foreground truncate">{organization.email}</span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={control}
              name="phoneNumber"
              // rules={{
              //   validate: (value) => {
              //     if (!value) return t('PHONE_NUMBER_REQUIRED');
              //     if (!isPossiblePhoneNumber(value)) return t('PHONE_NUMBER_LENGTH_INVALID');
              //     if (!isValidPhoneNumber(value)) return t('INVALID_PHONE_NUMBER');
              //     return true;
              //   },
              // }}
              render={({ field }) => (
                <FormItem>
                  <Label>{t('MOBILE_NO')}.</Label>
                  <FormControl>
                    <UIPhoneInput
                      {...field}
                      onChange={(value: Value) => setValue('phoneNumber', value ?? '')}
                      placeholder={t('ENTER_YOUR_MOBILE_NUMBER')}
                      defaultCountry="CH"
                      countryCallingCodeEditable={false}
                      international
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="country"
              rules={{ required: t('COUNTRY_REQUIRED') || 'Country is required' }}
              render={({ field }) => (
                <FormItem>
                  <Label>{t('COUNTRY')}*</Label>
                  <FormControl>
                    <CountrySelect
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t('SELECT_COUNTRY') || 'Select your country'}
                      className="rounded-md"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <DialogFooter className="mt-5 flex justify-end gap-2">
            <Button
              variant="outline"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
            >
              {t('CANCEL')}
            </Button>
            <Button
              type="submit"
              loading={isPending}
              disabled={isPending || !isFormChanged}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {t('SAVE')}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  );
};
